import { useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { Copy, ExternalLink, Loader2, Send } from 'lucide-react';
import { useOptionalGmailConnection } from './GmailConnectionContext';
import { LanguageToggle } from './LanguageToggle';
import { resendLinkAction } from './resendLinkAction';
import type { PendingAction } from './usePendingActions';
import { COLORS } from './format';
import {
  resendExpiredInvite,
  type ExpiredInvite,
  type ResendResult,
} from '../../services/interviewLinksApi';

type Lang = 'de' | 'en';

/**
 * "Interview link expired — send a new link" — candidates whose booking window
 * closed without a booking, who still have their one second chance.
 *
 * "Send new link" mints a fresh booking link (which spends the chance — the
 * candidate then drops off this list) and opens a popup with the follow-up email
 * pre-drafted from the library's reminder blocks. Sending stays manual: the
 * recruiter copies the text or opens it in their mail client.
 *
 * ── Two callers, one row ────────────────────────────────────────────────────
 *
 * `ExpiredLinkRows` renders a given list and is used INSIDE a role card, which
 * is where these belong: the standalone block above the accordions was reported
 * as easy to miss, because it named the interview link's candidate-facing role
 * ("Test Role") while the card below named the Lever posting ("Test Opportunity
 * _ for automation"), so nothing tied the two together. See useExpiredLinks.
 *
 * `ExpiredLinksSection` keeps the standalone block for entries that match no
 * role card — they still hold a dead link and must not vanish.
 *
 * Renders nothing when there is nothing to nudge, so it never adds noise.
 */
export function ExpiredLinkRows({
  items,
  /** The role card's own title, shown instead of the link's, so the two agree. */
  roleTitle,
  onSpent,
  onDefer,
}: {
  items: ExpiredInvite[];
  roleTitle?: string;
  onSpent?: () => void;
  /** Queue the send behind the board's countdown — see resendLinkAction. */
  onDefer?: (action: Omit<PendingAction, 'secondsLeft'>) => void;
}) {
  const [active, setActive] = useState<{ item: ExpiredInvite; result: ResendResult } | null>(null);
  const [busyToken, setBusyToken] = useState<string | null>(null);
  /**
   * ⚠️ The MODAL must outlive the list, and this early return is why.
   *
   * Minting a link spends the second chance, so the candidate leaves this list —
   * and when they were the only entry, `items` became empty, this component
   * returned null, and the modal it owned was unmounted in the same tick it
   * opened. The recruiter saw the row vanish and no popup at all, with no chance
   * to confirm or stop anything (reported Sep 2026). Refreshing is now deferred
   * to the modal's own close, and the guard covers only the LIST.
   */
  if (items.length === 0 && !active) return null;

  async function sendNewLink(item: ExpiredInvite) {
    setBusyToken(item.token);
    try {
      const result = await resendExpiredInvite(item.linkId, item.token);
      // NOT refreshed here — see the guard above.
      setActive({ item, result });
    } catch (e) {
      showToast({
        status: 'error',
        description: e instanceof Error ? e.message : 'Could not send a new link.',
      });
    } finally {
      setBusyToken(null);
    }
  }

  return (
    <div className="space-y-2">
      {items.length > 0 && (
        <>
      <div className="text-[11px] font-semibold tracking-wide" style={{ color: COLORS.orangeText }}>
        Interview link expired · send a new link
      </div>
      <div className="space-y-2 rounded-lg p-3" style={{ backgroundColor: COLORS.cotton }}>
        {items.map((item) => (
          <div
            key={item.token}
            className="flex flex-wrap items-center gap-3 rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-gray-200"
          >
            <div className="min-w-0 basis-56">
              <div className="flex items-center gap-2 text-sm">
                <span
                  aria-hidden
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: COLORS.statusOrange }}
                />
                <span className="truncate font-semibold text-gray-900">{item.candidateName}</span>
                {item.leverUrl && (
                  <a
                    href={item.leverUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Lever ↗
                  </a>
                )}
              </div>
              <div className="mt-0.5 truncate pl-6 text-xs text-gray-400">
                {[roleTitle || item.roleTitle, item.stageName].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div className="flex min-w-0 flex-1 items-center gap-2 text-xs text-gray-500">
              link expired, never booked
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={busyToken === item.token || !item.linkActive}
                onClick={() => void sendNewLink(item)}
                title={item.linkActive ? undefined : 'The interview link is closed — reopen it first.'}
              >
                {busyToken === item.token ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="mr-1 h-3.5 w-3.5" />
                )}
                Send new link
              </Button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400">
        Each candidate gets one second chance — sending a new link here uses it, and they drop off
        this list.
      </p>
        </>
      )}
      {active && (
        <ResendModal
          item={active.item}
          result={active.result}
          onDefer={onDefer}
          /*
            The same draft, put back after a failed send — the toast tells the
            recruiter to copy it, so it has to be there to copy.

            Functional, and it YIELDS to whatever is open. The failure lands ten
            seconds later, by which time the recruiter may have opened and edited
            a second candidate's email; overwriting that would destroy their
            typing to show them an older draft.
          */
          onReopen={() => setActive((cur) => cur ?? active)}
          /*
            The list is refreshed when the SEND RESOLVES, never on open and never
            on close.

            Refreshing earlier empties the list — the spent invite leaves it —
            and an empty list unmounts these rows, taking the `active` state with
            them. The deferred failure callback then has nothing left to restore
            the draft into. Keeping the row until the email has actually gone is
            also the honest state: the candidate has not been told anything yet.
          */
          onSent={onSpent}
          onClose={() => setActive(null)}
        />
      )}
    </div>
  );
}

/**
 * The leftovers: expired links whose posting matches no role card on this board.
 *
 * Kept as its own block rather than dropped. A recruiter can hold a dead link for
 * a role that is not on their board — a colleague's posting, or one with no other
 * SLA item — and tidying the layout must not lose a candidate waiting on a link
 * that no longer works. The heading says why they are out here, so this no longer
 * reads as "a list that stands apart for no reason".
 */
export function ExpiredLinksSection({
  items,
  onSpent,
  onDefer,
}: {
  items: ExpiredInvite[];
  onSpent?: () => void;
  onDefer?: (action: Omit<PendingAction, 'secondsLeft'>) => void;
}) {
  /*
    NO early return around ExpiredLinkRows, for the same reason it has none
    around its own modal: unmounting it discards the resend state, and a
    deferred failure would have nothing to restore the draft into. Rows render
    null when there is nothing to show, so an empty list still renders nothing —
    a Fragment rather than a div, so "nothing" really is nothing.
  */
  return (
    <>
      {items.length > 0 && (
        <p className="text-xs text-gray-500">
          Expired interview links on roles that are not on this board:
        </p>
      )}
      <ExpiredLinkRows items={items} onSpent={onSpent} onDefer={onDefer} />
    </>
  );
}

/** The popup: the freshly-minted link plus the pre-drafted follow-up email. */
function ResendModal({
  item,
  result,
  onClose,
  onDefer,
  onReopen,
  onSent,
}: {
  item: ExpiredInvite;
  result: ResendResult;
  onClose: () => void;
  onDefer?: (action: Omit<PendingAction, 'secondsLeft'>) => void;
  /** Re-open this same draft — used when the queued send fails. */
  onReopen?: () => void;
  /** The email actually went — refresh the list, which now legitimately loses the row. */
  onSent?: () => void;
}) {
  const gmail = useOptionalGmailConnection();
  // The link's own language, not a fixed German: the toggle is for the
  // exception, and this dialog is one link so there is exactly one right answer.
  const [lang, setLang] = useState<Lang>(item.language ?? 'de');
  const [subject, setSubject] = useState({
    de: result.email.de.subject,
    en: result.email.en.subject,
  });
  const [body, setBody] = useState({ de: result.email.de.body, en: result.email.en.body });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${subject[lang]}\n\n${body[lang]}`);
      showToast({ status: 'success', description: 'Email copied to clipboard.' });
    } catch {
      showToast({ status: 'error', description: 'Could not copy — select and copy manually.' });
    }
  };

  const mailto = `mailto:${encodeURIComponent(item.candidateEmail)}?subject=${encodeURIComponent(
    subject[lang]
  )}&body=${encodeURIComponent(body[lang])}`;

  /**
   * Sending needs a connected Gmail AND a Lever opportunity: `/send-email`
   * resolves the recipient from Lever rather than trusting the client, so an
   * invite with no opportunity id cannot be addressed server-side.
   */
  const canSend = Boolean(gmail?.connected && item.opportunityId && onDefer);

  /**
   * Hands the send to the board's countdown and closes — exactly like the Move
   * and Archive modals. The banner at the top of the board is then the place to
   * stop it, so there is one Stop for every outbound email rather than a
   * different rule for this one.
   */
  const send = () => {
    if (!canSend || !onDefer) return;
    onDefer(
      resendLinkAction({
        item,
        subject: subject[lang],
        body: body[lang],
        gmailToken: gmail?.token ?? null,
        onSent,
        // Closing straight away is deliberate — the Stop button lives in the
        // board's countdown banner, and a modal on top of it would hide the one
        // control this whole delay exists for. The cost is that a failure lands
        // after the draft is gone, so a failure puts it back.
        onFailed: onReopen,
      })
    );
    onClose();
  };

  return (
    <Modal open onOpenChange={(open: boolean) => !open && onClose()}>
      <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,44rem)] gap-4 overflow-auto rounded p-6 md:p-8">
        <ModalHeader>
          <ModalTitle>New booking link for {item.candidateName}</ModalTitle>
        </ModalHeader>

        {/*
          AMBER, not green, and it leads with what has NOT happened.
          A green "a fresh link was generated" read as "done" — the second chance
          was spent at this point and the candidate had been told nothing, which
          is exactly how one ended up holding a link nobody had sent him
          (reported Sep 2026). The link is real; the email is the missing half.
        */}
        <div
          className="rounded-xl p-3 text-xs"
          style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
        >
          <strong>Not sent yet.</strong> A fresh booking link exists and this used{' '}
          {item.candidateName.split(' ')[0]}’s one second chance — but they will not hear about it
          until this email goes out.
          <div className="mt-1 break-all font-mono text-[11px]">{result.invite.bookingUrl}</div>
        </div>

        {/* No `hasEdits` guard: subject and body are held PER LANGUAGE, so
            switching shows the other draft rather than replacing this one, and
            an edit made in either survives. */}
        <LanguageToggle value={lang} onChange={setLang} />

        <Input
          label="Subject"
          value={subject[lang]}
          onChange={(e) => setSubject({ ...subject, [lang]: (e.target as HTMLInputElement).value })}
        />
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-700">Email body</span>
          <textarea
            className="min-h-[16rem] w-full rounded-xl border border-gray-200 p-3 font-mono text-[13px]"
            value={body[lang]}
            onChange={(e) => setBody({ ...body, [lang]: e.target.value })}
          />
        </label>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button size="sm" variant="outline" onClick={() => void copy()}>
            <Copy className="mr-1 h-3.5 w-3.5" /> Copy email
          </Button>
          <a href={mailto}>
            <Button size="sm" variant="outline">
              <ExternalLink className="mr-1 h-3.5 w-3.5" /> Open in email
            </Button>
          </a>
          {/*
            The PRIMARY action, and the one that was missing. Every other
            candidate email on this board goes from the recruiter's own Gmail and
            lands in the Log; this one offered copy/paste only, so a recruiter who
            read "Send new link" reasonably believed it had been sent.
            Falls back to the manual buttons above when Gmail is not connected or
            the candidate has no Lever opportunity to resolve an address from.
          */}
          {canSend && (
            <Button size="sm" onClick={send}>
              <Send className="mr-1 h-3.5 w-3.5" /> Send to {item.candidateEmail}
            </Button>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
}
